// TODO: Migrate from GA Universal Analytics (`ga()`) to the dataLayer → GTM → GA4 pipeline
// using `handleCustomUDL` from @utils/UDLUtils. Requires coordination with Web Analytics team
// to confirm GA4 event mappings and ensure no tracking gaps before removing `ga()` calls.

/** Custom event names consumed by TrackingController — import these in other modules. */
export const TRACKING_EVENTS = {
  RE_INIT: "trackingController:reInit",
} as const;

export function dispatchReInitTracking(): void {
  document.dispatchEvent(new CustomEvent(TRACKING_EVENTS.RE_INIT));
}

declare global {
  interface Window {
    ga?: (...args: unknown[]) => void;
    ga_debug?: { trace: boolean };
  }
  function ga(...args: unknown[]): void;
}

export class TrackingController {
  private el: HTMLElement;
  private analyticskey: string;
  private domainname: string;
  private anonymizeip: boolean;
  private allowcrosssitetracking: boolean;
  private crosstrackingdomain: string;
  private resourcetrackingBasepath: string;
  private debug = false;
  private boundScan: () => void;

  constructor(el: HTMLElement) {
    this.el = el;
    this.analyticskey = el.dataset.analyticskey ?? "";
    this.domainname = el.dataset.domainname ?? "";
    this.anonymizeip = el.dataset.anonymizeip === "true";
    this.allowcrosssitetracking = el.dataset.allowcrosssitetracking === "true";
    this.crosstrackingdomain = el.dataset.crosstrackingdomain ?? "";
    this.resourcetrackingBasepath = el.dataset.resourcetrackingBasepath ?? "";
    this.boundScan = () => this.scan();
  }

  init(): void {
    if (this.analyticskey) {
      this.initAnalytics();
    }
    this.scan();
    document.addEventListener(TRACKING_EVENTS.RE_INIT, this.boundScan);
  }

  destroy(): void {
    document.removeEventListener(TRACKING_EVENTS.RE_INIT, this.boundScan);
  }

  private initAnalytics(): void {
    // Load GA script (Universal Analytics)
    ((i: any, s: Document, o: string, g: string, r: string) => {
      i["GoogleAnalyticsObject"] = r;
      i[r] =
        i[r] ||
        function () {
          (i[r].q = i[r].q || []).push(arguments);
        };
      i[r].l = Date.now();
      const a = s.createElement(o) as HTMLScriptElement;
      const m = s.getElementsByTagName(o)[0];
      a.async = true;
      a.src = g;
      m?.parentNode?.insertBefore(a, m);
    })(
      window,
      document,
      "script",
      `https://www.google-analytics.com/analytics${this.debug ? "_debug" : ""}.js`,
      "ga",
    );

    if (typeof window.ga !== "undefined") {
      if (this.debug) {
        window.ga("set", "sendHitTask", null);
        window.ga_debug = { trace: true };
      }

      window.ga("create", this.analyticskey, "auto", {
        cookieDomain: this.domainname,
        allowLinker: true,
      });
      window.ga("send", "pageview");

      if (this.anonymizeip) {
        window.ga("set", "anonymizeIp", true);
      }
      if (this.allowcrosssitetracking) {
        window.ga("require", "linker");
        window.ga("linker:autoLink", this.crosstrackingdomain);
      }
    }
  }

  scan(): void {
    const resourceLinks = document.querySelectorAll<HTMLAnchorElement>(
      'a[data-resource-tracking]:not([data-has-analytics="true"])',
    );
    const newsPdfLinks = document.querySelectorAll<HTMLAnchorElement>(
      '.newsdownload a[href*=".pdf"]:not([data-has-analytics="true"])',
    );
    // TODO: (AB-8924) courseDetailPdfLinks and downloadListPdfLinks overlap with GA4 tracking in
    // c-course-detail-v2.js (handleCustomUDL "course_detail_download_click") and
    // c-downloadlist.js (handleCustomUDL "download_list_teaser_asset_open") respectively.
    // These selectors send ga() Universal Analytics events while the component JS sends GA4
    // dataLayer events — dual-tracking during UA→GA4 migration. Once UA is decommissioned,
    // remove courseDetailPdfLinks, downloadListPdfLinks, and downloadListZipForms from scan().
    const courseDetailPdfLinks = document.querySelectorAll<HTMLAnchorElement>(
      '.comp-course-detail a[href*=".pdf"]:not([data-has-analytics="true"])',
    );
    const downloadListPdfLinks = document.querySelectorAll<HTMLAnchorElement>(
      '.downloadlist a[href$=".pdf"]:not([data-has-analytics="true"])',
    );
    const downloadListZipForms = document.querySelectorAll<HTMLFormElement>(
      '.comp-downloadlist form:not([data-has-analytics="true"])',
    );

    resourceLinks.forEach((link) => {
      link.addEventListener("click", () => {
        const title = (link.dataset.resourceTrackingTitle ?? "").trim();
        const resourcePath = link.dataset.resourcePath ?? "";
        this.trackEvent("Download", "Resource Center", title, 0);

        const wrapper = link.closest(".js-resource-tracking") as HTMLElement | null;
        const pagePath = wrapper?.dataset.resourceTrackingPagePath ?? "";
        if (pagePath && this.resourcetrackingBasepath) {
          const params = new URLSearchParams({
            resourcepath: resourcePath,
            targetgrouppath: pagePath,
          });
          fetch(`${this.resourcetrackingBasepath}?${params}`, { method: "GET" }).catch(() => {
            // tracking errors can be ignored
          });
        }
      });
      link.setAttribute("data-has-analytics", "true");
    });

    newsPdfLinks.forEach((link) => {
      link.addEventListener("click", () => {
        this.trackEvent("Download", "News", link.textContent?.trim() ?? "", 0);
      });
      link.setAttribute("data-has-analytics", "true");
    });

    courseDetailPdfLinks.forEach((link) => {
      link.addEventListener("click", () => {
        this.trackEvent("Download", "Courses", link.textContent?.trim() ?? "", 0);
      });
      link.setAttribute("data-has-analytics", "true");
    });

    downloadListPdfLinks.forEach((link) => {
      link.addEventListener("click", () => {
        this.trackEvent("Download", "Download List", link.textContent?.trim() ?? "", 0);
      });
      link.setAttribute("data-has-analytics", "true");
    });

    downloadListZipForms.forEach((form) => {
      form.addEventListener("submit", () => {
        form
          .querySelectorAll<HTMLInputElement>(".teaser-entry input:checked")
          .forEach((checkbox) => {
            const entry = checkbox.closest(".teaser-entry");
            const title = entry?.querySelector("a")?.textContent?.trim() ?? "";
            this.trackEvent("Download", "Resource Center - Zip", title, 0);
          });
      });
      form.setAttribute("data-has-analytics", "true");
    });
  }

  private trackEvent(category: string, action: string, label: string, value: number): void {
    if (typeof window.ga !== "undefined") {
      const safeValue = parseInt(String(value)) > 0 ? parseInt(String(value)) : 0;
      window.ga("send", "event", category, action, label, safeValue);
    }
  }

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="tracking-controller"]')
      .forEach((el) => {
        new TrackingController(el).init();
      });
  }
}
